import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import MinimalNav from '../MinimalNav';
import Evelyn from '../Evelyn';
import TeamMemberCTA from '../TeamMemberCTA';
import FooterSection from '../Footer/FooterSection';

import planningJournal from '../../public/images/planning-journal-image.png';
import heartfeltDefaultImg from '../../public/images/heartfelt-funeral-home.svg';
import completeDefaultImg from '../../public/images/complete-funeral-home.svg';
import uniqueDefaultImg from '../../public/images/unique-funeral-home.svg';

import { useGetPreneedPackages, useRedirectToLastScreen } from '../../customHooks';
import { fetchClientConfig } from '@evdy/web-redux/dist/actions/fetchClientConfig';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';
import { useScrollToTop } from '@evdy/web-core/dist/customHooks';

import styles from './ThankYou.module.scss';

const { resetSubmitOrder } = authUserOperations;

const defaultPackageImg = [uniqueDefaultImg, heartfeltDefaultImg, completeDefaultImg];

const {
  'thank-you': thankYouClass,
  'custom-evelyn': customEvelynClass,
  'thank-you-content': thankYouContentClass,
  'custom-team-member': customTeamMemberClass,
  'order-info-container': orderInfoContainerClass,
  'package-info': packageInfoClass,
  'provider-info': providerInfoClass,
  'funeral-home-info': funeralHomeInfoClass,
  'home-image': homeImageClass,
  'home-text': homeTextClass,
  'order-info': orderInfoClass,
  'planning-journal': planningJournalClass
  // 'log-in-section': logInSectionClass
} = styles;

const ThankYou = ({ location }) => {
  const {
    authUser,
    clientConfig,
    preneedPackages = []
  } = useSelector(({ authUser, client, webPurchase }) => ({
    authUser: authUser,
    clientConfig: client?.config?.data,
    preneedPackages: webPurchase.preneedPackages.data?.packages
  }));

  const dispatch = useDispatch();

  // on mount, fetch client config
  // on unmount, reset order submitted flag
  useEffect(() => {
    dispatch(fetchClientConfig());
    return () => {
      dispatch(resetSubmitOrder());
    };
  }, []);

  // scroll to top on mount
  useScrollToTop();

  // get preneed packages
  useGetPreneedPackages();

  // redirect to your-funeral-package if page is reloaded
  useRedirectToLastScreen({});

  const { data: userData } = authUser || {};
  const { name: userName = {} } = userData || {};
  const { packageId: currentPackageId, funeralHome = {}, order } = userData?.preneed || {};

  const { orderId = '', submittedOn = '' } = order || {};

  const {
    name: funeralHomeName,
    profileImage,
    address: funeralHomeAddress,
    packageSetupComplete
  } = funeralHome || {};
  const { street = '', city = '', state = '', zip = '' } = funeralHomeAddress || {};
  const bottomAddressString = `${city ? `${city},` : ''}${state ? ` ${state}` : ''}${
    zip ? ` ${zip}` : ''
  }`;

  const currentUserPackage = preneedPackages?.find(pkg => currentPackageId === pkg.packageId);
  const { name, packageSubtitle } = currentUserPackage || {};

  const { planningTeamMember } = clientConfig?.preneed || {};
  const { title, name: teamMemberName, description, cta } = planningTeamMember || {};
  const { preferred, nonPreferred } = description;

  const isPreferred = packageSetupComplete;

  const evelynArray = [
    {
      content: `Thank you${
        userName.first ? ' ' + userName?.first : ''
      }! Your order has been received.`,
      style: 'header'
    },
    'We’re processing your paperwork and will follow up with an email that includes your confirmation and next steps to finalize your order.'
  ];

  const formattedSubmitDate = submittedOn && format(new Date(submittedOn), 'MMM d, yyyy');

  return (
    <div className={thankYouClass}>
      <MinimalNav pageCopy="Order Completed" {...{ location }} />
      <div className={thankYouContentClass}>
        <Evelyn alignBubbles speechBubbleArray={evelynArray} customClass={customEvelynClass} />
        <TeamMemberCTA
          customClass={customTeamMemberClass}
          titleCopy={title}
          name={teamMemberName}
          description={isPreferred ? preferred : nonPreferred}
          ctaCopy={cta}
        />
        <div className={orderInfoContainerClass}>
          <div className={packageInfoClass}>
            <h2>{name}</h2>
            <p>{packageSubtitle}</p>
          </div>
          <div className={providerInfoClass}>
            <span>Provider</span>
            <div className={funeralHomeInfoClass}>
              <div className={homeImageClass}>
                <img src={profileImage || defaultPackageImg[+currentPackageId]} alt="profile" />
              </div>
              <div className={homeTextClass}>
                <h4>{funeralHomeName}</h4>
                <p>{street}</p>
                <p>{bottomAddressString}</p>
              </div>
            </div>
          </div>
          <div className={orderInfoClass}>
            <div>
              <span>Date Ordered</span>
              <p>{formattedSubmitDate}</p>
            </div>
            <div>
              <span>Order Id</span>
              <p>{orderId}</p>
            </div>
          </div>
        </div>
        <div className={planningJournalClass}>
          <img src={planningJournal} alt="planning journal preview" />
          <div>
            <h4>Your Life Planning Journal</h4>
            <p>
              Capture your wisdom, memories &amp; final wishes with our complimentary Life Planning
              Journal.
            </p>
            <a
              href="https://everdays-resources.s3.amazonaws.com/Everdays_LifePlanningJournal.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Get Journal
            </a>
          </div>
        </div>
        {/* <div className={logInSectionClass}>
          <img src={placeholderProfile} alt="generic profile" />
          <p>
            Log in to Everdays with your email address to access your account and package details
            any time.
          </p>
        </div> */}
      </div>
      <FooterSection />
    </div>
  );
};

export default ThankYou;
